import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TableComponent from '../table/TableComponent';
import CustomPagination from '../pagination/CustomPagination.js';
import BulkTransactionDetailModal from './BulkTransactionDetailModal.js';
import { apiService } from '../../../../common/apiCallService.js';
import { getCurrencySymbol } from '../../../helpers.js';
import JncLoading from '../pagination/JncLoading.js';
import PropTypes from 'prop-types';
import DataTable from '../../../../../new-ui/components/DataTable.jsx';
import Pagination from '../../../../../new-ui/components/Pagination.jsx';

const FALLBACK = 'N/A';

const renderCell =
    (field, fallback = FALLBACK) =>
    (row, index) => (
        <div data-e2e={row[field] || fallback} data-e2e-row={index} data-tag="allowRowEvents">
            {row[field] || fallback}
        </div>
    );

const columnFields = [
    'accountId',
    'type',
    'currencyType',
    'currency',
    'amount',
    'beneficiaryName',
    'beneficiaryAddress',
    'beneficiaryAccountNumber',
    'beneficiaryCountry',
    'bankName',
    'bankAddress',
    'bankCountry',
    'sortCode',
    'iban',
    'swift',
    'additionalInfo',
    'reference',
];

const getColumns = () =>
    columnFields.map(field => ({
        key: field,
        label:
            field == 'iban'
                ? field.toUpperCase()
                : field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        selector: row => row[field] || FALLBACK,
        sortable: field === 'currency' || field === 'amount',
        renderCell:
            field === 'amount'
                ? (row, index) => {
                      const val = parseFloat(row.amount);
                      return (
                          <div
                              data-e2e={row.amount || FALLBACK}
                              data-e2e-row={index}
                              data-tag="allowRowEvents">
                              {isNaN(val) ? FALLBACK : val.toFixed(2)}
                          </div>
                      );
                  }
                : renderCell(field),
    }));

const AmountSummaryDesktop = ({ amountSummary, fileName }) => (
    <div className="bg-bulkpay-card-bg border-[1px] rounded-t-lg h-[170px] py-3 px-6 border-bulkpay-preview-table-border">
        <h2 className="text-sm font-semibold mb-3 font-inter uppercase text-text leading-tight ">{fileName || FALLBACK}</h2>
        <div className="w-full p-4 bg-[#F4F4F5] dark:bg-[#37383C] flex overflow-x-auto hide-scrollbar h-[84x] rounded-lg">
            {amountSummary.map((item, index) => (
                <React.Fragment key={index}>
                    <div
                        className={`flex ${
                            index === 0 ? 'pl-0 pr-4' : 'px-4'
                        } flex-1 justify-start`}>
                        <div className="text-left w-max">
                            <p className="font-inter font-semibold text-sm mb-1 leading-tight uppercase text-text">
                                {item.text === 'Transactions'
                                    ? item.amount
                                    : `${getCurrencySymbol(
                                          item.text === 'Total Amount' ? 'USD' : item.text,
                                      )} ${
                                          !isNaN(item.amount)
                                              ? Number(item.amount).toFixed(2)
                                              : '0.00'
                                      }`}
                            </p>
                            <p className="font-inter font-medium uppercase text-xs leading-none text-balanceCard-manage">
                                {item.text}
                            </p>
                        </div>
                    </div>
                    {index < amountSummary.length - 1 && (
                        <Divider className="text-section-title bg-section-title h-auto w-px" />
                    )}
                </React.Fragment>
            ))}
        </div>
    </div>
);

AmountSummaryDesktop.propTypes = {
    amountSummary: PropTypes.array.isRequired,
    fileName: PropTypes.string,
};

const AmountSummaryMobile = ({ amountSummary }) => (
    <div className="bg-bulkpay-card-bg border-bulkpay-preview-table-border rounded-t-lg">
        <div className="w-full p-3 bg-[#F4F4F5] dark:bg-[#37383C] flex flex-col space-y-4 rounded-lg">
            {amountSummary.map((item, index) => (
                <>
                    <div key={index} className="flex items-center justify-between">
                        <div className="text-left flex-1">
                            <p className="font-inter font-semibold text-base text-balanceCard-manage uppercase">
                                {item.text}
                            </p>
                        </div>
                        <div className="text-right flex-1">
                            <p className="font-inter font-medium text-base text-text">
                                {item.amount}
                            </p>
                        </div>
                    </div>
                    {index < amountSummary.length - 1 && (
                        <div className="self-stretch h-px relative bg-input-border" />
                    )}
                </>
            ))}
        </div>
    </div>
);

AmountSummaryMobile.propTypes = {
    amountSummary: PropTypes.array.isRequired,
};

const ReviewTable = ({ transactionId }) => {
    const api_url = useSelector(state => state.config.api_url);
    const clientData = useSelector(state => state.juno.clientData);

    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [openModal, setOpenModal] = useState(false);
    const [selectedData, setSelectedData] = useState({});
    const [data, setData] = useState([]);
    const [amountSummary, setAmountSummary] = useState([]);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState([]);

    const rowsPerPage = 10;
    const handleOnClose = () => {
        setOpenModal(false);
        setSelectedData({});
    };

    useEffect(() => {
        const fetchTransactions = async () => {
            setLoading(true);
            try {
                await apiService(
                    (window.location.hostname === 'localhost' ? api_url : '') +
                        '/restapi/bulk/fetch-transactions',
                    {
                        email: clientData.clientEmail,
                        transactionId,
                        page: currentPage,
                        pageSize: rowsPerPage,
                    },
                    res => {
                        if (res?.success) {
                            setData(res.data);
                            setAmountSummary(res.totalAmount?.filter(Boolean) || []);
                            setTotalRows(res.data.length);
                            setResponse(res);
                        }
                    },
                    errMessage => console.error(errMessage),
                );
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, [currentPage, transactionId]);

    return (
        <div className="lg:p-0 border-bulkpay-preview-table-border rounded-xl w-full lg:w-[90%] bg-bulkpay-card-bg mx-auto">
            {/* Desktop Summary */}
            <div className="mt-8 lg:block hidden w-full text-section-title bg-bulkpay-card-bg">
                <div className="flex flex-col justify-between ">
                    <AmountSummaryDesktop
                        amountSummary={amountSummary}
                        fileName={response?.fileName}
                    />
                </div>
            </div>

            {/* Mobile Summary */}
            <div className="lg:hidden block w-full text-section-title mt-1 p-3">
                <div className="flex justify-between items-center mb-3 uppercase">
                    <h2 className="text-lg font-semibold font-inter">{response?.fileName}</h2>
                </div>
                <AmountSummaryMobile amountSummary={amountSummary} />
            </div>

            {/* Desktop Table */}
            <div className="border-b-1 border-l-1 border-r-1 rounded-lg lg:block hidden border-bulkpay-preview-table-border">
                <DataTable
                    data={data}
                    columns={getColumns()}
                    loading={loading}
                    customRenderCell={(row, data, index) => {
                        if (row.key === 'amount') {
                            const val = parseFloat(data.amount);
                            console.log(val);
                            return (
                                <div
                                    data-e2e={data.amount || FALLBACK}
                                    data-e2e-row={index}
                                    data-tag="allowRowEvents">
                                    {isNaN(val) ? FALLBACK : val.toFixed(2)}
                                </div>
                            );
                        }

                        if (data[row.key] == undefined) {
                            return 'N/A';
                        }

                        return null;
                    }}
                />
                <div className="flex justify-center py-2">
                    <Pagination
                        data={data}
                        totalCount={response.count}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={response.count ? rowsPerPage : 0}
                    />
                </div>
            </div>

            <div className="w-full bg-bulkpay-card-bg lg:hidden relative">
                {!loading ? (
                    data.map((obj, idx) => (
                        <div
                            key={idx}
                            className="flex justify-between items-center p-4 border-b-[1px] border-b-card-bg last:border-none"
                            onClick={() => {
                                setSelectedData(obj);
                                setOpenModal(true);
                            }}>
                            <div className="flex flex-col w-[90%]">
                                <span className="text-sm leading-tight font-medium text-balanceCard-manage truncate uppercase">
                                    {obj.beneficiaryName || 'Unnamed Beneficiary'}
                                </span>
                                <span className="text-sm font-semibold leading-tight text-text uppercase">
                                    {obj.type}
                                </span>
                            </div>
                            <span className="text-text text-sm leading-tight font-semibold uppercase">
                                {getCurrencySymbol(obj.currency || 'USD')}
                                {!isNaN(parseFloat(obj.amount))
                                    ? parseFloat(obj.amount).toFixed(2)
                                    : '0.00'}
                            </span>
                            <ArrowForwardIosIcon className="text-gray-400" fontSize="small" />
                        </div>
                    ))
                ) : (
                    <JncLoading />
                )}
                {response?.count ? (
                    <div className='flex justify-center py-2 mb-[40px]'>
                    <Pagination
                        data={data}
                        totalCount={response.count}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={response.count ? rowsPerPage : 0}
                    />
                    </div>
                ) : null}
                <br />
            </div>

            {/* Modal */}
            <BulkTransactionDetailModal open={openModal} close={handleOnClose} data={selectedData} />
        </div>
    );
};

export default ReviewTable;
